import {
    useCallback,
    useContext
} from 'react';
import {
    collection,
    addDoc,
    serverTimestamp,
} from 'firebase/firestore';

import { firestore } from '../../services/startFirestore';
import { AgentType, DocDataWithId, DocId } from '#types/System.types';
import { UserContext } from '../../providers/UserContext';
import { MessageContext } from '../../providers/MessageContext';

/**
 * Interface for data add operations
 * @property {function} add - Function to add data to Firestore
 */
export interface UseAdd {
    /**
     * Adds data to Firestore
     * @param path - The Firestore collection path.
     * @param data - The data to add.
     * @param agentType - Optional agent type for metadata (defaults to User).
     * @returns The document ID.
     */
    add: <T extends DocDataWithId = DocDataWithId>(
        path: string, 
        data: Omit<T, 'docId'>,
        agentType?: AgentType
    ) => Promise<DocId>;
}

/**
 * Hook for managing Firestore data add operations
 * @param firestore - Firestore instance
 * @returns Object with add function
 */
export const useAdd = (): UseAdd => {
    const { user } = useContext(UserContext);
    const { handleFirestoreWriteError } = useContext(MessageContext);

    const add = useCallback(async <T extends DocDataWithId = DocDataWithId>(
        path: string,
        data: Omit<T, 'docId'>,
        agentType?: AgentType
    ): Promise<DocId> => {
        const collectionRef = collection(firestore, path);
        const dataWithMeta = {
            ...data,
            meta: {
                created: serverTimestamp(),
                lastModified: serverTimestamp(),
                userId: user?.uid,
                agentType: agentType ?? AgentType.User,
            }
        };
        try {
            const docRef = await addDoc(collectionRef, dataWithMeta);
            return docRef.id;
        } catch (error) {
            throw handleFirestoreWriteError(error, '[useAdd] add');
        }
    }, [handleFirestoreWriteError, user?.uid]);

    return { add };
};